import { File, Paths } from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, TouchableOpacity } from 'react-native';
import { useShallow } from 'zustand/shallow';

import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { showAlert, SvgIcon, Text, WebView } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import type { AuthStackScreenProps } from '@/navigations/types';
import { useUiStore } from '@/stores';
import { colors } from '@/theme/colors';

type PlayerPathwayWebViewScreenProps = AuthStackScreenProps<'PlayerPathwayWebView'>;

/**
 * Build a safe `.pdf` filename from the Joomag download URL.
 * The signed CDN URL carries a human-readable `name=` query param; fall back to
 * the URL path segment otherwise.
 */
function derivePdfFileName(url: string): string {
  let base = '';
  const nameMatch = url.match(/[?&]name=([^&]+)/i);
  if (nameMatch) {
    try {
      base = decodeURIComponent(nameMatch[1].replace(/\+/g, ' '));
    } catch {
      base = nameMatch[1];
    }
  }
  if (!base) {
    const path = url.split(/[?#]/)[0];
    base = path.substring(path.lastIndexOf('/') + 1);
  }
  base = base.replace(/[\\/:*?"<>|]+/g, '_').trim();
  if (!base) base = 'download';
  if (!/\.pdf$/i.test(base)) base += '.pdf';
  return base;
}

export function PlayerPathwayWebViewScreen({ navigation, route }: PlayerPathwayWebViewScreenProps) {
  const styles = useStyles();
  const { scale, isTablet, deviceWidth, deviceHeight } = useScaling();
  const [loading, setLoading] = useState(false);
  const didFinishInitialLoadRef = useRef(false);
  const { url } = route.params;
  const isPortrait = deviceHeight >= deviceWidth;

  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  // iOS: WKWebView renders PDFs inline, so a download tap never saves the file.
  // `onFileDownload` fires (the response is an attachment) once a handler is set;
  // we fetch the file and hand it to the share sheet ("Save to Files"). Android
  // keeps downloading natively via DownloadManager — `onFileDownload` is iOS-only.
  const handleFileDownload = useCallback(
    async (downloadUrl: string) => {
      const targetUrl = downloadUrl?.trim();
      if (!targetUrl) return;
      showGlobalLoading();
      try {
        const destination = new File(Paths.cache, derivePdfFileName(targetUrl));
        const file = await File.downloadFileAsync(targetUrl, destination, { idempotent: true });
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(file.uri, {
            mimeType: 'application/pdf',
            UTI: 'com.adobe.pdf',
          });
        }
      } catch {
        showAlert({
          title: STRINGS.common.playerPathwayScreen.download.errorTitle,
          message: STRINGS.common.playerPathwayScreen.download.errorMessage,
        });
      } finally {
        hideGlobalLoading();
      }
    },
    [hideGlobalLoading, showGlobalLoading],
  );

  useEffect(() => {
    didFinishInitialLoadRef.current = false;
    setLoading(false);
  }, [url]);

  return (
    <Stack flex={1} backgroundColor={'#141A26'}>
      <AppHeader backgroundColor={colors['0']}>
        <AppHeader.Left>
          <TouchableOpacity activeOpacity={0.6} onPress={() => navigation.goBack()}>
            <Stack direction="row" align="center" gap={scale(2)}>
              <SvgIcon name="IcBackBlue" size={scale(24)} />
              <Text
                variant={
                  isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                }
                style={styles.headerActionText}>
                {STRINGS.player.mutatePlayer.header.back}
              </Text>
            </Stack>
          </TouchableOpacity>
        </AppHeader.Left>

        <AppHeader.Title>
          <Text
            variant={
              isTablet
                ? 'Font Inter/Subtitle/Bold 14px'
                : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
            }
            fontWeight={isTablet ? undefined : '800'}
            style={styles.headerTitle}>
            {STRINGS.common.playerPathwayScreen.title}
          </Text>
        </AppHeader.Title>

        <AppHeader.Right>
          <Stack
            width={scale({ phone: 57, tablet: 60 })}
            height={scale({ phone: 28, tablet: 30 })}
          />
        </AppHeader.Right>
      </AppHeader>

      <Stack flex={1} style={styles.webviewContainer}>
        {loading && (
          <Stack style={styles.loadingOverlay} pointerEvents="none">
            <ActivityIndicator size="large" color={colors.Blue} />
          </Stack>
        )}

        <WebView
          source={{ uri: url }}
          style={styles.webview}
          isTablet={isTablet}
          forceMobileOnTablet
          forceMobileViewportWidth={isTablet && isPortrait ? 430 : undefined}
          onFileDownload={({ nativeEvent }) => {
            handleFileDownload(nativeEvent.downloadUrl);
          }}
          onLoadStart={() => {
            if (!didFinishInitialLoadRef.current) setLoading(true);
          }}
          onLoadEnd={() => {
            didFinishInitialLoadRef.current = true;
            setLoading(false);
          }}
          onError={() => {
            didFinishInitialLoadRef.current = true;
            setLoading(false);
          }}
        />
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(() => ({
  headerActionText: {
    color: colors.Blue,
  },
  headerTitle: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  webviewContainer: {
    flex: 1,
    backgroundColor: '#141A26',
  },
  webview: {
    flex: 1,
    zIndex: 9,
    backgroundColor: 'transparent',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
}));
